import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import { SITE } from "@/lib/site";
import { hreflangLinks } from "@/lib/hreflang";
import {
  pageSchemaScript,
  PERSON_ID,
  PORTRAIT_ID,
  SITE_URL,
  PRIMARY_PORTRAIT_PATH,
  PRIMARY_PORTRAIT_WIDTH,
  PRIMARY_PORTRAIT_HEIGHT,
} from "@/seo/schema";

const PATH = "/security-consultant-in-india";
const URL = `${SITE_URL}${PATH}`;
const TITLE = "Security Consultant in India | Arif Khan";
const DESC =
  "Arif Khan is a security consultant in India with 23+ years in electronic security and perimeter protection, advising architects, developers and owners.";

const FAQS = [
  {
    q: "What does a security consultant do?",
    a: "A security consultant assesses risk, defines what the security systems must achieve, designs the system architecture and coordinates it with the architect and other services, before any product is bought or installed.",
  },
  {
    q: "When should I bring in a security consultant?",
    a: "Ideally at concept or early design stage, before drawings, ceilings and cable routes are frozen. Late involvement usually means retrofit costs, visible devices and coverage gaps.",
  },
  {
    q: "Is a security consultant different from a security integrator?",
    a: "Yes. The consultant is responsible for design intent and specification; the integrator supplies, installs and commissions the systems. Good projects need both, with clear responsibilities.",
  },
  {
    q: "What types of projects does Arif Khan advise on?",
    a: "Luxury residences and farmhouses, corporate offices, industrial facilities, institutions, government and critical-infrastructure sites, covering CCTV, access control, intrusion detection, perimeter protection and command-and-control integration.",
  },
  {
    q: "Where is Arif Khan based?",
    a: `${SITE.location}`,
  },
  {
    q: "How do I discuss a project with Arif Khan?",
    a: `Use the contact page, email ${SITE.email} or call ${SITE.phone}. Sharing drawings, site location and project stage helps the first conversation.`,
  },
];

export const Route = createFileRoute("/security-consultant-in-india")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangLinks(PATH)],
    scripts: [
      pageSchemaScript(
        {
          path: PATH,
          type: "WebPage",
          name: TITLE,
          description: DESC,
          breadcrumb: [{ name: "Security Consultant in India" }],
          extra: {
            about: { "@id": PERSON_ID },
            mainEntity: { "@id": PERSON_ID },
            primaryImageOfPage: { "@id": PORTRAIT_ID },
          },
        },
        [
          {
            "@type": "FAQPage",
            "@id": `${URL}#faq`,
            isPartOf: { "@id": `${URL}#webpage` },
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ],
      ),
    ],
  }),
});

const PROOF = [
  { value: "23+", label: "Years" },
  { value: "1,000+", label: "Projects" },
  { value: "500 km+", label: "Perimeter Security" },
];
const PROOF_LINE = "23+ Years · 1,000+ Projects · 500 km+ Perimeter Security";

const WHEN = [
  "The site is large, open or hard to monitor, such as campuses, estates, plants and infrastructure.",
  "Several systems (CCTV, access control, intrusion, perimeter, command centre) must work together.",
  "The architect needs security coordinated into drawings before ceilings, walls and cable routes are frozen.",
  "The owner wants an outcome-based specification that more than one integrator can price fairly.",
  "An existing system records but does not produce usable evidence or timely alerts.",
];

const MISTAKES = [
  { t: "Luxury residences & farmhouses", b: "Security planned after interiors are frozen, leading to visible devices, retrofit costs and weak perimeter coverage." },
  { t: "Corporate offices", b: "Access control and CCTV bought as separate packages that never share events or reports." },
  { t: "Industrial facilities", b: "Perimeter treated as a civil item. A boundary wall defines the line but does not detect intrusion." },
  { t: "Government & institutions", b: "Camera counts used as the measure of success instead of detection, verification and response." },
  { t: "Critical infrastructure", b: "Environmental false alarms, such as vegetation, weather and wildlife, not designed out, so operators stop trusting alerts." },
];

const STAGES = [
  { n: "01", t: "Assessment", b: "Understand the site, risks, operations and people. Define what each system must achieve." },
  { n: "02", t: "Design", b: "System architecture, device layouts, infrastructure needs and an outcome-based specification." },
  { n: "03", t: "Coordination", b: "Work with the architect, MEP and interior teams so power, conduits, racks and mounting are built in." },
  { n: "04", t: "Implementation & verification", b: "Support the integrator during installation and check field of view, detection and recording against the design." },
];

const TECH = [
  { t: "CCTV & video surveillance", b: "Cameras placed for identification and evidence, with planned recording and retention.", to: "/solutions/$slug", slug: "cctv-command-centre" },
  { t: "Access control", b: "Who can go where and when, using cards, biometrics or mobile credentials.", to: "/solutions/$slug", slug: "access-control-biometrics" },
  { t: "Intrusion detection & perimeter protection", b: "Detecting, verifying and delaying intrusion at the boundary.", to: "/solutions/$slug", slug: "perimeter-protection" },
  { t: "Command & control integration", b: "One place where alarms, video and access events are seen and acted on.", to: "/solutions/$slug", slug: "cctv-command-centre" },
  { t: "Entrance, gate & parking", b: "Boom barriers, ANPR and vehicle control at entries.", to: "/solutions/$slug", slug: "entrance-gate-parking-automation" },
  { t: "Associated infrastructure", b: "Power backup, network, cabling and rack space that keep every system running.", to: "/solutions/$slug", slug: "security-infrastructure" },
] as const;

const SECTORS = [
  { label: "Luxury residences", slug: "luxury-residences" },
  { label: "Farmhouses & private estates", slug: "farmhouses-private-estates" },
  { label: "Corporate offices", slug: "corporate-offices" },
  { label: "Industrial facilities", slug: "industrial-facilities" },
  { label: "Warehouses & logistics", slug: "warehouses-logistics" },
  { label: "Government & infrastructure", slug: "government-infrastructure" },
  { label: "Education & auditoriums", slug: "education-auditoriums" },
  { label: "Developers & real estate", slug: "developers-real-estate" },
];

function Section({ id, title, children, alt }: { id: string; title: string; children: React.ReactNode; alt?: boolean }) {
  return (
    <section aria-labelledby={id} className={`py-20 md:py-24 ${alt ? "border-y border-border bg-surface" : ""}`}>
      <div className="container-page">
        <h2 id={id} className="text-3xl md:text-4xl font-semibold tracking-tight max-w-3xl">{title}</h2>
        <div className="mt-8">{children}</div>
      </div>
    </section>
  );
}

function Page() {
  return (
    <PageShell>
      <section className="relative pt-40 pb-20 border-b border-border bg-surface">
        <div className="container-page grid gap-12 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <span className="eyebrow">Security Consulting · India</span>
            <h1 className="mt-5 text-balance text-5xl md:text-6xl font-semibold tracking-tight leading-[0.95]">
              Security Consultant in India
            </h1>
            <p className="mt-6 text-lg text-ink-soft leading-relaxed max-w-2xl">
              Arif Khan is a New Delhi-based security consultant with 23+ years in electronic security and perimeter protection. He works with architects, developers, corporate and institutional owners, government bodies and private homeowners across India to plan CCTV, access control, intrusion detection, perimeter and command-centre systems around each site’s real risks, before products are selected.
            </p>
            <p className="mt-3 text-sm text-ink-soft">BU Head, Security &amp; Automation, Altima Global · Projects across India</p>
            <p className="mt-2 text-sm font-medium">{PROOF_LINE}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="tap-target inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">
                Discuss a Project <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
              <Link to="/about" className="tap-target inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium">
                About Arif Khan
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img
              src={PRIMARY_PORTRAIT_PATH}
              alt="Arif Khan, security consultant in India and BU Head, Security & Automation at Altima Global"
              width={PRIMARY_PORTRAIT_WIDTH}
              height={PRIMARY_PORTRAIT_HEIGHT}
              loading="eager"
              decoding="async"
              className="w-full aspect-[4/5] object-cover object-[62%_30%] rounded-3xl border border-border"
            />
          </div>
        </div>
      </section>

      <Section id="credibility" title="Experience at a glance">
        <dl className="grid gap-6 sm:grid-cols-3">
          {PROOF.map((s) => (
            <div key={s.label} className="rounded-2xl border border-border p-6">
              <dt className="text-sm text-ink-soft">{s.label}</dt>
              <dd className="mt-2 text-4xl font-semibold tracking-tight">{s.value}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section id="when" title="When does a project need a security consultant?" alt>
        <p className="max-w-3xl text-lg text-ink-soft">A project needs a security consultant when the risks, the site or the number of systems are too complex to leave to product selection alone. Typical signs:</p>
        <ul className="mt-6 grid gap-3 max-w-3xl list-disc pl-5 text-ink-soft">
          {WHEN.map((w) => <li key={w}>{w}</li>)}
        </ul>
      </Section>

      <Section id="difference" title="Security planning before product selection">
        <p className="max-w-3xl text-lg text-ink-soft">Good security projects decide what the systems must achieve before choosing products. Two complementary roles make that happen.</p>
        <div className="mt-8 grid gap-6 md:grid-cols-2 max-w-5xl">
          <div className="rounded-2xl border border-border p-6">
            <h3 className="text-lg font-semibold">Consulting &amp; Design</h3>
            <p className="mt-3 text-ink-soft">Sets the risks, performance requirements, system architecture and specification, and coordinates them with the architect and other services.</p>
          </div>
          <div className="rounded-2xl border border-border p-6">
            <h3 className="text-lg font-semibold">Integration &amp; Execution</h3>
            <p className="mt-3 text-ink-soft">Brings product and site expertise to supply, install, program, commission and maintain the systems so the design works in daily operation.</p>
          </div>
        </div>
        <p className="mt-6 max-w-3xl text-ink-soft">Depending on the project, Arif Khan and Altima Global may support design, implementation or both.</p>
      </Section>

      <Section id="mistakes" title="Common security planning mistakes by sector" alt>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {MISTAKES.map((m) => (
            <div key={m.t} className="rounded-2xl border border-border bg-background p-6">
              <h3 className="text-lg font-semibold">{m.t}</h3>
              <p className="mt-3 text-ink-soft">{m.b}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="method" title="A four-stage consulting method">
        <ol className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {STAGES.map((s) => (
            <li key={s.n} className="rounded-2xl border border-border p-6">
              <span className="eyebrow">{s.n}</span>
              <h3 className="mt-2 text-lg font-semibold">{s.t}</h3>
              <p className="mt-3 text-ink-soft">{s.b}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section id="tech" title="Security technologies covered" alt>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {TECH.map((t) => (
            <Link key={t.t} to={t.to} params={{ slug: t.slug }} className="group rounded-2xl border border-border bg-background p-6 hover:border-primary">
              <h3 className="text-lg font-semibold">{t.t}</h3>
              <p className="mt-3 text-ink-soft">{t.b}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">Learn more <ArrowRight className="h-4 w-4" aria-hidden /></span>
            </Link>
          ))}
        </div>
      </Section>

      <Section id="sectors" title="Sectors and project environments">
        <ul className="flex flex-wrap gap-3">
          {SECTORS.map((s) => (
            <li key={s.slug}>
              <Link to="/industries/$slug" params={{ slug: s.slug }} className="tap-target inline-flex rounded-full border border-border px-4 py-2 text-sm hover:border-primary">{s.label}</Link>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="work" title="Selected project experience" alt>
        <div className="grid gap-6 md:grid-cols-2 max-w-4xl">
          {/* [PROOF NEEDED] INS Rajali Naval Air Station (15 km perimeter): omitted until project name, distance and permission to reference are confirmed. */}
          <div className="rounded-2xl border border-border bg-background p-6">
            <span className="eyebrow">Luxury Residences · Delhi NCR</span>
            <h3 className="mt-2 text-lg font-semibold">Residences &amp; farmhouses for HNI clients</h3>
            <p className="mt-3 text-ink-soft">Multi-year engagements covering estate security and automation, planned before interiors are frozen.</p>
          </div>
          <div className="rounded-2xl border border-border bg-background p-6">
            <span className="eyebrow">More work</span>
            <h3 className="mt-2 text-lg font-semibold">Projects portfolio</h3>
            <p className="mt-3 text-ink-soft">See landmark engagements across security, automation and AV.</p>
            <Link to="/projects" className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary">View projects <ArrowRight className="h-4 w-4" aria-hidden /></Link>
          </div>
        </div>
      </Section>

      <Section id="faq" title="Frequently asked questions">
        <div className="max-w-3xl divide-y divide-border border-y border-border">
          {FAQS.map((f) => (
            <div key={f.q} className="py-6">
              <h3 className="text-lg font-semibold">{f.q}</h3>
              <p className="mt-2 text-ink-soft">{f.a}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-ink-soft">
          Related: <Link to="/locations/security-consultant-delhi-ncr" className="text-primary">Security consultant in Delhi NCR</Link> ·{" "}
          <Link to="/ask-arif/$slug" params={{ slug: "how-to-choose-a-security-integrator" }} className="text-primary">How to choose a security integrator</Link> ·{" "}
          <Link to="/ask-arif/$slug" params={{ slug: "cctv-planning-guide-for-architects" }} className="text-primary">CCTV planning guide for architects</Link> ·{" "}
          <Link to="/for-architects" className="text-primary">For Architects</Link>
        </p>
      </Section>

      <section aria-labelledby="cta" className="py-24 border-t border-border bg-surface">
        <div className="container-page text-center max-w-3xl">
          <h2 id="cta" className="text-3xl md:text-4xl font-semibold tracking-tight">Planning security for a project?</h2>
          <p className="mt-4 text-ink-soft">Share your drawings, site and project stage for security-planning input.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="tap-target inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">Discuss a Project <ArrowRight className="h-4 w-4" aria-hidden /></Link>
            <Link to="/for-architects" className="tap-target inline-flex items-center rounded-full border border-border px-6 py-3 text-sm font-medium">For Architects</Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
